import React from "react";
import { Text, makeStyles, tokens } from "@fluentui/react-components";
import { AccountBalanceDto } from "../../../services/xero-integration.service";
import { formatCurrency } from "../../../utils/formatters";
import { useTypographyStyles } from "../../../styles/typography.styles";

interface AccountBalanceDetailsProps {
  accounts: AccountBalanceDto[];
}

const useStyles = makeStyles({
  container: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalXXL,
    borderRadius: tokens.borderRadiusMedium,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    textAlign: "left",
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  thRight: {
    textAlign: "right",
  },
  td: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  tdRight: {
    textAlign: "right",
    fontFamily: "monospace",
  },
  accountCode: {
    color: tokens.colorNeutralForeground2,
    marginRight: tokens.spacingHorizontalS,
  },
  emptyMessage: {
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
});

export const AccountBalanceDetails: React.FC<AccountBalanceDetailsProps> = ({ accounts }) => {
  const styles = useStyles();
  const typography = useTypographyStyles();

  if (accounts.length === 0) {
    return (
      <div className={styles.container}>
        <Text className={`${typography.bodySmall} ${styles.emptyMessage}`}>
          No account details available
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Account</th>
            <th className={styles.th}>Type</th>
            <th className={`${styles.th} ${styles.thRight}`}>Debit</th>
            <th className={`${styles.th} ${styles.thRight}`}>Credit</th>
            <th className={`${styles.th} ${styles.thRight}`}>Balance</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account) => (
            <tr key={account.accountCode}>
              <td className={styles.td}>
                <Text className={typography.bodySmall}>
                  <span className={styles.accountCode}>{account.accountCode}</span>
                  {account.accountName}
                </Text>
              </td>
              <td className={styles.td}>
                <Text className={typography.bodySmall}>{account.accountType}</Text>
              </td>
              <td className={`${styles.td} ${styles.tdRight}`}>
                <Text className={typography.bodySmall}>{account.debit > 0 ? formatCurrency(account.debit) : "-"}</Text>
              </td>
              <td className={`${styles.td} ${styles.tdRight}`}>
                <Text className={typography.bodySmall}>{account.credit > 0 ? formatCurrency(account.credit) : "-"}</Text>
              </td>
              <td className={`${styles.td} ${styles.tdRight}`}>
                <Text className={typography.bodySmallStrong}>
                  {formatCurrency(account.balance)}
                </Text>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
